import { useResumeData } from "../hooks/useResumeData";
import { useEffect, useState } from "react";
import { FaEnvelope, FaLinkedin, FaGithub } from "react-icons/fa";
import { getIcon, getDownloadIcon } from "../utils/icons";
import workdayLogo from "../assets/workday-logo.webp";
import calpolyLogo from "../assets/calpoly-logo.webp";
import Section from "./Section";
import TimelineItem from "./TimelineItem";
import Tag from "./Tag";
import ProjectCard from "./ProjectCard";

const Resume = () => {
  const { resume, loading, error } = useResumeData();
  const [projectsExpanded, setProjectsExpanded] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (resume.basics.name) {
      document.title = resume.basics.name;
    }
  }, [resume.basics.name]);

  const downloadResume = async () => {
    try {
      const res = await fetch("/resume.json");
      if (!res.ok) throw new Error(`HTTP ${String(res.status)}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "michael-van-beek-resume.json";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      console.error("Failed to download resume:", err);
    }
  };

  return (
    <div className="bg-black text-white min-h-screen p-10 font-sans">
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>
      <div className="max-w-3xl mx-auto">
        {loading ? (
          <div className="flex items-center justify-center min-h-[50vh]">
            <p className="text-gray-400 animate-pulse">Loading...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
            <p className="text-red-400 font-medium">Failed to load resume data</p>
            <p className="text-gray-500 text-sm mt-2">{error}</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <header className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <img
                  src={resume.basics.image}
                  alt={resume.basics.name}
                  width="80"
                  height="80"
                  className="h-20 w-20 rounded-full object-cover"
                />
                <div>
                  <h1 className="text-3xl font-bold">{resume.basics.name}</h1>
                  <p className="text-gray-400">{resume.basics.label}</p>
                </div>
              </div>

              {/* Contact Icons */}
              <nav aria-label="Contact links" className="flex space-x-4">
                {resume.basics.email && (
                  <a
                    href={`mailto:${resume.basics.email}`}
                    className="text-gray-400 hover:text-white"
                    aria-label="Send email"
                  >
                    <FaEnvelope size={20} />
                  </a>
                )}
                {resume.basics.linkedin && (
                  <a
                    href={resume.basics.linkedin}
                    className="text-gray-400 hover:text-white"
                    aria-label="LinkedIn profile"
                  >
                    <FaLinkedin size={20} />
                  </a>
                )}
                {resume.basics.github && (
                  <a
                    href={resume.basics.github}
                    className="text-gray-400 hover:text-white"
                    aria-label="GitHub profile"
                  >
                    <FaGithub size={20} />
                  </a>
                )}

                {/* Download resume JSON */}
                <button
                  onClick={() => void downloadResume()}
                  title="Download resume JSON"
                  aria-label="Download resume JSON"
                  className="text-gray-400 hover:text-white"
                >
                  {getDownloadIcon({ size: 20 })}
                </button>
              </nav>
            </header>

            <main id="main-content">
              {/* About */}
              <Section title="About">
                <p className="text-gray-300">{resume.basics.summary}</p>
              </Section>

              {/* Experience */}
              <Section title="Experience">
                <div className="border-l-2 border-gray-600 pl-6 relative ml-8">
                  {resume.work.map((job) => (
                    <TimelineItem
                      key={job.name}
                      logo={workdayLogo}
                      title={job.name}
                      subtitle={job.position}
                      dateRange={`${job.startDate} - ${job.endDate || "Present"}`}
                      description={job.summary}
                    />
                  ))}
                </div>
              </Section>

              {/* Education */}
              <Section title="Education">
                <div className="border-l-2 border-gray-600 pl-6 relative ml-8">
                  {resume.education.map((edu) => {
                    return (
                      <TimelineItem
                        key={edu.institution}
                        logo={calpolyLogo}
                        title={edu.institution}
                        subtitle={`${edu.studyType} in ${edu.area}`}
                        dateRange={edu.endDate ?? ""}
                        description={
                          edu.courses && edu.courses.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {edu.courses.map((c) => (
                                <Tag key={c} name={c} icon={getIcon(c, { size: 14 })} />
                              ))}
                            </div>
                          ) : undefined
                        }
                      />
                    );
                  })}
                </div>
              </Section>

              {/* Skills */}
              <Section title="Technical Skills">
                <div className="flex flex-wrap gap-2">
                  {resume.skills.map((skill) => (
                    <Tag
                      key={skill.name}
                      name={skill.name}
                      icon={getIcon(skill.name, { size: 16 })}
                    />
                  ))}
                </div>
              </Section>

              {/* Interests */}
              <Section title="Interests">
                <div className="flex flex-wrap gap-2">
                  {resume.interests.map((interest) => (
                    <Tag
                      key={interest.name}
                      name={interest.name}
                      icon={getIcon(interest.name, { size: 16 })}
                    />
                  ))}
                </div>
              </Section>

              {/* Projects */}
              <Section
                title="Projects"
                action={
                  <button
                    onClick={() => {
                      setProjectsExpanded((prev) => !prev);
                    }}
                    className="text-xs text-sky-400 hover:text-sky-300 transition-colors"
                  >
                    {projectsExpanded ? "Collapse All" : "Expand All"}
                  </button>
                }
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {resume.projects.map((project) => (
                    <ProjectCard
                      key={project.name}
                      title={project.name}
                      description={project.description || ""}
                      url={project.url}
                      icon={getIcon(project.name, { size: 20 })}
                      keywords={project.keywords}
                      forceExpanded={projectsExpanded}
                    />
                  ))}
                </div>
              </Section>
            </main>
          </>
        )}
      </div>
    </div>
  );
};

export default Resume;
